import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CloudUpload, Info, Upload } from 'lucide-react'
import { Card } from '@/components/shadcn/card'
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/shadcn/popover'

interface Props {
  /** File selection (drag & drop or click). Assignment server-side by personnel number. */
  onFiles: (files: FileList) => void
}

/**
 * Global upload card (top of the page). Dumb: accepts files via drag & drop
 * or click and passes them up — the page opens the upload dialog.
 */
export function GlobalUploadCard({ onFiles }: Props) {
  const { t } = useTranslation('service-modules-personal-documents')
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files)
  }

  return (
    <Card
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }}
      onDragLeave={(e) => {
        e.preventDefault(); e.stopPropagation()
        // only reset when we really leave the card (not into a child)
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false)
      }}
      onDrop={handleDrop}
      className={`items-center gap-3 border-dashed p-6 text-center transition-colors ${
        isDragging ? 'border-primary bg-primary/5' : ''
      }`}
    >
      <CloudUpload className="size-12 text-primary" />
      <h3 className="text-lg font-semibold">{t('personalDocuments.globalUploadTitle')}</h3>

      <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
        <Popover>
          <PopoverTrigger
            aria-label={t('personalDocuments.globalUploadTooltip')}
            className="text-primary hover:opacity-80"
          >
            <Info className="size-4" />
          </PopoverTrigger>
          <PopoverContent align="start" className="flex flex-col gap-2 text-left text-sm">
            <p className="font-semibold text-primary">{t('personalDocuments.popoverTitle')}</p>
            <p>{t('personalDocuments.popoverFormat')}</p>
            <p>{t('personalDocuments.popoverExample')}</p>
            <p>{t('personalDocuments.popoverAssignment')}</p>
          </PopoverContent>
        </Popover>
        <span>{t('personalDocuments.globalUploadInfo')}</span>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80"
      >
        <Upload className="size-4" />
        {t('personalDocuments.dropOrSelect')} <span className="underline">{t('personalDocuments.select')}</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onFiles(e.target.files)
          e.target.value = '' // erlaubt erneutes Wählen derselben Datei
        }}
      />
    </Card>
  )
}
